import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, Quote } from "lucide-react";
import Navbar from "../components/Navbar";
import useSeo from "../hooks/useSeo";

/**
 * Customers — testimonial page.
 * The audit flagged D3.2 Story proof at 0/5. Stubbed with 10 named
 * testimonial slots. Founder needs to email the first 20 paying customers
 * for one-line testimonials with first name, photo (public/customers/*),
 * and outcome-specific quotes — "I caught ASTS before it ran 17%" beats
 * "great product".
 *
 * TODO(founder): replace [TESTIMONIAL_STUB] entries with real quotes.
 */
export default function Customers() {
  useSeo({
    title: "Customers — Moonbucks",
    description: "What real Moonbucks subscribers say about the alerts they've caught. Outcome-specific quotes from paying customers.",
    canonical: "https://moonbucks.io/customers",
  });

  // [TESTIMONIAL_STUB] Replace each with a real subscriber quote:
  //   { name, role, photo (public/customers/*.jpg), quote, outcome }
  // Keep outcome-specific (name the trade + return) not generic praise.
  const testimonials = Array.from({ length: 10 }).map((_, i) => ({
    id: i + 1,
    name: `[Customer ${i + 1} name]`,
    role: "[City · Retail investor]",
    photo: `/customers/${i + 1}.jpg`,
    quote: "[TESTIMONIAL_STUB — outcome-specific quote e.g. \"I caught ASTS on the Steady alert and rode it to +17% in a week.\"]",
    outcome: "[Ticker · Return]",
  }));

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-6" data-testid="customers-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>

        <div className="label-uppercase text-mb-emerald mb-3">Customers</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">
          The people who caught the move.
        </h1>
        <p className="mt-4 text-neutral-600 text-lg max-w-2xl leading-relaxed">
          Every quote below names a real subscriber, a real trade, and a real outcome. If you want proof the tool works, this is it — no cherry-picking, no anonymous "@trader247" screenshots.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-grid">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-white border border-mb-border rounded-lg p-6" data-testid={`customer-card-${t.id}`}>
              <Quote className="w-5 h-5 text-mb-emerald/40" />
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed italic">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-mb-emerald/10 overflow-hidden flex-shrink-0">
                  <img src={t.photo} alt={`${t.name} — Moonbucks subscriber`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
                <div>
                  <div className="font-display font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-neutral-500">{t.role}</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-mb-border/50 text-[11px] font-mono-data text-mb-emerald">
                {t.outcome}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-mb-emerald/5 border border-mb-emerald rounded-lg p-6 text-sm text-neutral-700">
          <div className="flex items-start gap-3">
            <Star className="w-5 h-5 text-mb-emerald flex-shrink-0 mt-0.5" />
            <div>
              Ratings and reviews collecting on <span className="italic">Trustpilot</span> and <span className="italic">Product Hunt</span> — link goes live once we clear our first 10 reviews. If you're a subscriber and want to leave one, reply to any alert email or write <a href="mailto:hello@moonbucks.io" className="text-mb-emerald font-semibold hover:underline">hello@moonbucks.io</a>.
            </div>
          </div>
        </div>

        <p className="mt-8 text-xs text-neutral-400 italic max-w-2xl">
          Testimonials describe individual subscriber experience. Past performance is not predictive of future results. Moonbucks is educational — not investment advice.
        </p>
      </div>
    </div>
  );
}
